import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Modal, Progress, Skeleton, Space, Typography, message } from 'antd';
import { DollarCircleOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  cancelRunJob,
  isJobUnfinished,
  listRunJobs,
  newRequestKey,
  rerunJob,
  type ValidationRunJob,
  type ValidationRunJobState,
} from '@/api/validationRuns';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

dayjs.extend(relativeTime);

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// RunRequestsPanel — EVAL-06 slice 6. Every Run click for one agent, newest first: is it waiting, running and
// how far, stopping, done, refused, cancelled or failed — and the run it produced. A waiting or running request
// can be cancelled; a finished one can be run again. The states are the server's (ValidationRunJobRules); this
// panel only words them. It polls while any request is unfinished and stops when none is.
// ─────────────────────────────────────────────────────────────────────────────

const POLL_MS = 5000;

const STATE_WORDS: Record<ValidationRunJobState, { label: string; tone: PillTone }> = {
  DispatchPending: { label: 'Not sent yet', tone: 'amber' },
  Queued: { label: 'Waiting to start', tone: 'slate' },
  Running: { label: 'Running', tone: 'violet' },
  CancelRequested: { label: 'Stopping', tone: 'amber' },
  Completed: { label: 'Done', tone: 'green' },
  Refused: { label: 'Refused', tone: 'red' },
  Cancelled: { label: 'Cancelled', tone: 'slate' },
  Failed: { label: 'Failed', tone: 'red' },
};

/** What the request asked for, in words. */
function scopeOf(job: ValidationRunJob): string {
  const what = job.scenarioId ? `Scenario ${job.scenarioId}` : 'All scenarios';
  const where =
    job.merchantId != null
      ? job.memberId != null
        ? ` · practice ${job.merchantId}, patient ${job.memberId}`
        : ` · practice ${job.merchantId}`
      : '';
  return what + where;
}

/** The line under a request that says why it is where it is, when that needs saying. */
function explanationOf(job: ValidationRunJob): { text: string; tone: 'danger' | 'warning' | 'secondary' } | null {
  if (job.refusalReason) return { text: job.refusalReason, tone: 'danger' };
  if (job.state === 'Failed') return { text: job.failureReason ?? 'The run failed.', tone: 'danger' };
  if (job.state === 'DispatchPending')
    return { text: job.failureReason ?? 'It will be sent again automatically.', tone: 'warning' };
  if (job.state === 'CancelRequested')
    return { text: 'It stops after the scenario it is running now; results so far are kept.', tone: 'warning' };
  if (job.state === 'Cancelled')
    return {
      text: job.validationRunId
        ? `Stopped after ${job.completedTrials} of ${job.expectedTrials ?? '?'} scenarios; their results are kept.`
        : 'Cancelled before any scenario ran.',
      tone: 'secondary',
    };
  return null;
}

export const RunRequestsPanel = observer(function RunRequestsPanel({
  agentId,
  refreshToken,
}: {
  agentId: number;
  refreshToken: number;
}) {
  const navigate = useNavigate();
  const links = useRunLinks();
  const { themeStore } = useStore();
  const divider = themeStore.mode === 'dark' ? colors.slate[700] : colors.slate[200];
  const [jobs, setJobs] = useState<ValidationRunJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<number | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const abort = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    window.clearTimeout(timer.current);
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    try {
      const list = await listRunJobs(agentId, 20, controller.signal);
      if (controller.signal.aborted) return;
      setJobs(list);
      setError(null);
      if (list.some((j) => isJobUnfinished(j.state))) timer.current = window.setTimeout(() => void load(), POLL_MS);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError((err as Error).message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    setJobs(null);
    void load();
    return () => {
      window.clearTimeout(timer.current);
      abort.current?.abort();
    };
  }, [load, refreshToken]);

  const confirmCancel = (job: ValidationRunJob) => {
    const running = job.state === 'Running';
    Modal.confirm({
      title: `Cancel run request #${job.id}?`,
      icon: <StopOutlined style={{ color: '#DC2626' }} />,
      okText: 'Yes, cancel it',
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      content: running
        ? 'It stops after the scenario it is running now. The results of the scenarios that finished are kept, and the run is marked as stopped, never as passed.'
        : 'It has not started, so nothing will run.',
      onOk: async () => {
        setActing(job.id);
        try {
          const resp = await cancelRunJob(job.id);
          void message.success(resp.message, 5);
          await load();
        } catch (err) {
          void message.error((err as Error).message);
        } finally {
          setActing(null);
        }
      },
    });
  };

  const confirmRerun = (job: ValidationRunJob) => {
    // One key per opened confirm: a doubled click on OK is the same rerun, never two.
    const key = newRequestKey();
    Modal.confirm({
      title: `Run request #${job.id} again?`,
      icon: <DollarCircleOutlined style={{ color: '#D97706' }} />,
      okText: 'Yes, run it again',
      cancelText: 'Cancel',
      content: (
        <div style={{ paddingTop: 8 }}>
          <p style={{ margin: '0 0 8px' }}>
            The same request ({scopeOf(job)}) runs again as a new one. <b>Real Foundry credits will be spent.</b>
          </p>
          <p style={{ margin: 0, color: '#6B7280', fontSize: 13 }}>
            It uses each scenario as it is now; if a scenario was edited since, the new run says which version ran.
          </p>
        </div>
      ),
      onOk: async () => {
        setActing(job.id);
        try {
          const resp = await rerunJob(job.id, key);
          void message.success(resp.message, 5);
          await load();
        } catch (err) {
          void message.error((err as Error).message + ' Retry from this dialog; it is the same request.');
          throw err; // review R10: keep the dialog, and its key
        } finally {
          setActing(null);
        }
      },
    });
  };

  return (
    <SectionCard
      title="Run requests"
      subtitle="Every run asked for this agent, newest first. A waiting or running request can be cancelled; a finished one can be run again."
      actions={
        <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>
          Refresh
        </Button>
      }
    >
      {error && <Alert type="error" showIcon title="Could not load run requests" description={error} style={{ marginBottom: 12 }} />}
      {jobs == null && !error ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : jobs && jobs.length === 0 ? (
        <Text type="secondary">No run requests for this agent yet. Use Run all scenarios, or Run on a scenario.</Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(jobs ?? []).map((job, i) => {
            const words = STATE_WORDS[job.state] ?? { label: job.state, tone: 'slate' as PillTone };
            const why = explanationOf(job);
            const showProgress = (job.state === 'Running' || job.state === 'CancelRequested') && (job.expectedTrials ?? 0) > 0;
            const unfinished = isJobUnfinished(job.state);
            return (
              <div
                key={job.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '8px 16px',
                  padding: '10px 0',
                  borderTop: i === 0 ? undefined : `1px solid ${divider}`,
                }}
              >
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <Space size={8} wrap>
                    <StatusPill tone={words.tone}>{words.label}</StatusPill>
                    <Text strong style={{ wordBreak: 'break-word' }}>{scopeOf(job)}</Text>
                  </Space>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {`#${job.id}`}
                      {job.kind === 'Rerun' && job.parentJobId != null ? ` · run again of #${job.parentJobId}` : ''}
                      {` · asked ${dayjs(job.submittedUtc).fromNow()}`}
                      {job.finishedUtc ? ` · ended ${dayjs(job.finishedUtc).fromNow()}` : ''}
                    </Text>
                  </div>
                  {showProgress && (
                    <div style={{ maxWidth: 320 }}>
                      <Progress
                        size="small"
                        percent={Math.round((100 * job.completedTrials) / (job.expectedTrials ?? 1))}
                        format={() => `${job.completedTrials} of ${job.expectedTrials}`}
                      />
                    </div>
                  )}
                  {why && (
                    <Text type={why.tone} style={{ fontSize: 12, display: 'block', wordBreak: 'break-word' }}>
                      {why.text}
                    </Text>
                  )}
                </div>
                <Space size={4} wrap>
                  {job.validationRunId != null && (
                    <Button size="small" onClick={() => navigate(links.run(job.validationRunId!))}>
                      Open run
                    </Button>
                  )}
                  {unfinished && job.state !== 'CancelRequested' && (
                    <Button size="small" danger loading={acting === job.id} onClick={() => confirmCancel(job)}>
                      Cancel
                    </Button>
                  )}
                  {!unfinished && (
                    <Button size="small" loading={acting === job.id} onClick={() => confirmRerun(job)}>
                      Run again
                    </Button>
                  )}
                </Space>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
});
